/**
 * TopicGroup — collapsible panel wrapping a cluster of related emails
 * within a domain (grouped by thread or normalised subject).
 */
import React from 'react';
import { ChevronDown, ChevronRight } from 'lucide-react';
import { cn, ICON_PX } from '../../../ui';
import { UrgencyIndicator } from '../shared/UrgencyIndicator';
import { TimeAgo } from '../shared/TimeAgo';
import { TopicGroupItem } from './TopicGroupItem';
import { TopicGroupItemActions } from './TopicGroupItemActions';

function TopicGroup({ group, isOpen, onToggle }) {
  return (
    <div className="rounded-lg border border-nn-border bg-nn-white overflow-hidden">
      {/* Header — clickable */}
      <button
        type="button"
        onClick={onToggle}
        className={cn(
          'flex items-center gap-2 w-full px-3 py-2 text-left transition-colors',
          'hover:bg-surface-warm',
          isOpen && 'bg-surface border-b border-nn-border',
        )}
      >
        {isOpen
          ? <ChevronDown size={ICON_PX.sm} className="text-text-muted shrink-0" />
          : <ChevronRight size={ICON_PX.sm} className="text-text-muted shrink-0" />
        }

        <span className={cn(
          'text-[12px] leading-normal flex-1 min-w-0 truncate',
          group.unreadCount > 0 ? 'font-medium text-text-dark' : 'text-text-dark-secondary',
        )}>
          {group.label}
        </span>

        <UrgencyIndicator urgency={group.highestUrgency} />

        {group.unreadCount > 0 && (
          <span className="shrink-0 text-[10px] font-bold leading-none px-1.5 py-0.5 rounded-full bg-aqua text-white">
            {group.unreadCount}
          </span>
        )}

        <span className="shrink-0 text-caption text-text-muted">
          {group.totalCount}
        </span>

        <TimeAgo date={group.latestReceivedAt} className="!text-body-sm" />
      </button>

      {/* Expanded content — email items + group-level actions */}
      {isOpen && (
        <div className="p-2 flex flex-col gap-1.5">
          {group.emails.map((email) => (
            <TopicGroupItem key={email.id} email={email} />
          ))}
          {/* Actions for the group — targets the latest email */}
          <TopicGroupItemActions email={group.emails[0]} scope="group" />
        </div>
      )}
    </div>
  );
}

export { TopicGroup };
